import Cookies from 'js-cookie';
import liff from '@line/liff';

import agentAPI from './agent_api';
import { isLiffApp } from './liff_helper';

import type { AxiosResponse } from 'axios';

/** 集客セグメントタイプ */
type SegmentType = 'organic' | 'affiliate' | 'advertisement' | 'cvm';

/** 問い合わせ送信時のオプション */
interface PostOptions {
  /** 問い合わせ時に選択した求人コード */
  positionCd?: string;
}

/** @class 問い合わせクラス */
export class Inquiry {
  /** @member 希望雇用形態コード */
  employmentTypeCd: string = null;
  /** @member 転職時期コード */
  wishfulTimeCd: string = null;
  /** @member 地方コード */
  areaCd: string = null;
  /** @member 都道府県コード */
  prefectureCd: number = null;
  /** @member 市区町村コード */
  cityCd: number = null;
  /** @member 業種コードリスト */
  positionTypeCds: string[] = [];
  /** @member 業種コード */
  businessCategoryCd: string = null;
  /** @member 調剤業務の経験有無 */
  dispensingExperience: boolean = null;
  /** @member 性別 */
  genderTypeCd: string = null;
  /** @member 年収 */
  annualIncome: number = null;
  /** @member 電話での連絡可否 */
  callAcceptance: boolean = null;
  /** @member 姓 */
  lastName: string = null;
  /** @member 名 */
  firstName: string = null;
  /** @member 姓ふりがな */
  lastNameKana: string = null;
  /** @member 名ふりがな */
  firstNameKana: string = null;
  /** @member 生まれ年コード */
  birthYearCd: string = null;
  /** @member 電話番号 */
  phone: string = null;
  /** @member メールアドレス */
  email: string = null;
  /** @member コメント */
  comment: string = null;
  /** @member 求職者ID */
  kyusyokusyaId: string = null;
  /** @member 希望勤務日数 */
  workFrequencyCd: string = null;
  /** @member 希望退勤時間 */
  leaveTimeCd: string = null;

  /** ページオプション */
  static get pageOptions() {
    return document.getElementById('page_options')?.dataset;
  }

  /** 集客セグメントタイプ */
  static get segmentType() {
    return Inquiry.pageOptions.segmentType as SegmentType;
  }

  /** 薬剤師資格をチェックするLPかどうか */
  static get pharmacistLicenseCheckLpFlag(): boolean {
    return Inquiry.pageOptions.pharmacistLicenseCheckLpFlag === 'true';
  }

  /** 雇用形態別LPかどうか */
  static get specializedJobTypeLpFlag(): boolean {
    return Inquiry.pageOptions.specializedJobTypeLpFlag === 'true';
  }

  /** 雇用形態別LPの正社員用LPかどうか */
  static get employmentTypeOnlyFull(): boolean {
    return Inquiry.pageOptions.employmentTypeOnlyFull === 'true';
  }

  /** 紹介予定派遣専用LPかどうか */
  static get employmentTypeOnlyPerm(): boolean {
    return Inquiry.pageOptions.employmentTypeOnlyPerm === 'true';
  }

  /** 正社員用の求人を表示するか */
  static get showFullTimePosition(): boolean {
    return Inquiry.pageOptions.showFullTimePosition === 'true';
  }

  constructor() {
    if(Inquiry.segmentType === 'cvm'){
      // 集客セグメントがCVMの場合、クエリパラメーターから都道府県コード・求職者IDを設定
      const queryParams = new URLSearchParams(window.location.search);
      this.kyusyokusyaId = queryParams.get('serial') || null;
      this.prefectureCd = Number(queryParams.get('prefecture')) || null;
    }
    else {
      // 集客セグメントがCVM以外の場合、Cookieの求人検索条件から都道府県コードの初期値を設定
      const cookie = Cookies.get('_pharmacist_conditions');
      if(cookie) {
        const conditions = JSON.parse(cookie);
        this.prefectureCd = Number(conditions.pref) || null;
      }
    }

    // ページオプションで問い合わせが設定されている場合は初期値を設定
    if(Inquiry.pageOptions.inquiry) {
      // ページオプションはJSONで渡されるため、パースする
      const inquiry = JSON.parse(Inquiry.pageOptions.inquiry);

      this.employmentTypeCd = inquiry.employment_type_cd || null;
      this.wishfulTimeCd = inquiry.wishful_time_cd || null;
    }
  }

  /**
   * コールバック関数がcatchになったらエラーページにリダイレクトする
   * @param callback コールバック関数(非同期)
   */
  protected async errorHandling<T>(callback: () => Promise<T>): Promise<T> {
    try {
      return await callback();
    } catch (error) {
      window.location.href = '/lps/cvm/error';
    }
  }

  /**
   * @method
   * 問い合わせを送信する
   */
  async post(options: PostOptions = {}) {
    return this.errorHandling(async () => {
      /** 問い合わせ送信時のレスポンス */
      let response: AxiosResponse<{ id: number }>;

      if(Inquiry.segmentType === 'cvm') {
        /** LINEアクセストークン */
        const lineAccessToken = this.getLineAccessToken();
  
        // 集客セグメントがCVMの場合、CVM用の問い合わせ送信
        response = await agentAPI.post(
          '/api/v1/cvm_inquiry',
          {
            inquiry: {
              wishful_time_cd: this.wishfulTimeCd,
              employment_type_cd: this.employmentTypeCd,
              kyusyokusya_id: this.kyusyokusyaId,
              comment: this.comment,
            },
            inquiry_inflow: {
              page_form: Inquiry.pageOptions.sfPageFormNo,
              position_cd: options.positionCd || null
            },
            line_access_token: lineAccessToken
          }
        );
      }
      else {
        /** LINEアクセストークン */
        const lineAccessToken = this.getLineAccessToken();

        // 集客セグメントがCVM以外の場合、通常の問い合わせ送信
        response = await agentAPI.post(
          '/api/v1/pharmacist_inquiry',
          {
            last_name: this.lastName,
            first_name: this.firstName,
            last_name_kana: this.lastNameKana,
            first_name_kana: this.firstNameKana,
            email: this.email,
            phone: this.phone,
            prefecture_code: this.prefectureCd,
            city_code: this.cityCd,
            birth_year_cd: this.birthYearCd,
            employment_type_cd: this.employmentTypeCd,
            position_type_cds: this.positionTypeCds,
            wishful_time_cd: this.wishfulTimeCd,
            comment: this.combinedComment,
            page_form: Inquiry.pageOptions.sfPageFormNo,
            sf_position_id: Inquiry.pageOptions.sfPositionId || null,
            sf_inquiry_type: Inquiry.pageOptions.sfInquiryType || null,
            line_access_token: lineAccessToken
          }
        );
      }

      if(liff.isInClient()) {
        // LIFFアプリの場合、送信成功したらブラウザを閉じる
        liff.closeWindow();
      }
      else {
        // 送信成功したらサンクスページにリダイレクトする
        this.redirectToThanks(response.data.id);
      }
    });
  }

  /**
   * @method
   * 問い合わせを送信する(登録地域変更付き)
   */
  async postCvmArea() {
    return this.errorHandling(async () => {
      /** LINEアクセストークン */
      const lineAccessToken = this.getLineAccessToken();

      // 問い合わせ送信
      const response = await agentAPI.post(
        '/api/v1/cvm_area_inquiry',
          {
          inquiry: {
            kyusyokusya_id: this.kyusyokusyaId,
            prefecture_code: this.prefectureCd,
            city_code: this.cityCd,
            employment_type_cd: this.employmentTypeCd
          },
          inquiry_inflow: {
            page_form: Inquiry.pageOptions.sfPageFormNo
          },
          line_access_token: lineAccessToken
        }
      );

      if(liff.isInClient()) {
        // LIFFアプリの場合、送信成功したらブラウザを閉じる
        liff.closeWindow();
      }
      else {
        // 送信成功したらサンクスページにリダイレクトする
        this.redirectToThanks(response.data.id);
      }
    });
  }

  /**
   * @method
   * 問い合わせを送信する
   */
  async postShindanInquiry() {
    return this.errorHandling(async () => {
      // 問い合わせ送信
      const response = await agentAPI.post(
        '/api/v1/shindan_inquiry',
        {
          last_name: this.lastName,
          first_name: this.firstName,
          last_name_kana: this.lastNameKana,
          first_name_kana: this.firstNameKana,
          email: this.email,
          phone: this.phone,
          prefecture_code: this.prefectureCd,
          city_code: this.cityCd,
          birth_year_cd: this.birthYearCd,
          employment_type_cd: this.employmentTypeCd,
          position_type_cds: this.positionTypeCds,
          wishful_time_cd: this.wishfulTimeCd,
          comment: this.combinedComment,
          page_form: Inquiry.pageOptions.sfPageFormNo,
          call_acceptance_flag: this.callAcceptance
        }
      );

      this.redirectToShindanThanks(response.data.id);
    });
  }

  /**
   * @method
   * 登録地域を変更する
   * - AGT-1146 No178 CVM_ワンクリックLPデザイン改修（登録＋住所変更）
   */
  async changeArea() {
    return this.errorHandling(async () => {
      // 地域変更APIを実行
      await agentAPI.post('/api/v1/cvm_change_area', {
        kyusyokusya_id: this.kyusyokusyaId,
        prefecture_code: this.prefectureCd,
        city_code: this.cityCd
      });

      // 成功したら登録完了画面にリダイレクト
      window.location.href = '/lps/cvm/area_changed';
    });
  }

  /**
   * @method
   * サンクスページにリダイレクト
   * @param id 問い合わせUID
   */
  redirectToThanks(uid: number | string) {
    // リダイレクト先のクエリ文字列
    const params = new URLSearchParams;

    // 問い合わせID
    params.append('uid', uid.toString());

    // アフィリエイトの場合
    if(Inquiry.segmentType === 'affiliate') {
      // 雇用形態
      params.append('recruitment_aff', this.affiliateEmploymentTypeParam);
      // 年齢
      params.append('age', this.affiliateAgeParam);
      // エリア
      params.append('area', this.affiliateAreaParam);
    }

    // リダイレクト
    window.location.href = `${Inquiry.pageOptions.redirectUrl}?${params}`;
  }

  /**
   * @method
   * 年収診断サンクスページにリダイレクト
   * @param id 問い合わせUID
   */
  redirectToShindanThanks(uid: number | string) {
    // リダイレクト先のクエリ文字列
    const params = new URLSearchParams;

    // 問い合わせID
    params.append('uid', uid.toString());
    params.append('category_business_cd', this.businessCategoryCd.toString());
    params.append('dispensing_experience', this.dispensingExperience.toString());
    params.append('annual_income', this.annualIncome.toString());
    params.append('gender_check', this.genderTypeCd.toString());

    // リダイレクト
    window.location.href = `${Inquiry.pageOptions.redirectUrl}?${params}`;
  }

  /** LIFFアプリの場合はLINEアクセストークンを返す */
  private getLineAccessToken(): string | undefined {
    if(isLiffApp()) {
      return liff.getAccessToken();
    }
  }

  /** (アフィリエイト用パラメーター)雇用形態 */
  protected get affiliateEmploymentTypeParam() {
    switch(this.employmentTypeCd) {
      case 'PART':
      case 'SPART':
      case 'NONE':
        return 'parttimer';
      case 'TEMP':
      case 'PERM':
        return 'haken';
      case 'FULL':
      default:
        return 'fulltime';
    };
  }

  /** (アフィリエイト用パラメーター)年齢 */
  protected get affiliateAgeParam() {
    const age = new Date().getFullYear() - Number(this.birthYearCd);

    switch(true) {
      // 20代・30代
      case age >= 20 && age < 40:
        return 'young';
      // 40代・50代
      case age >= 40 && age < 60:
        return 'middle';
      // その他
      default:
        return 'other';
    };
  }

  /** (アフィリエイト用パラメーター)エリア */
  protected get affiliateAreaParam() {
    // 埼玉・千葉・東京・神奈川・大阪・兵庫
    if([11, 12, 13, 14, 27, 28].includes(this.prefectureCd)) {
      return '1';
    }
    else {
      return '2';
    }
  }

  /** 問い合わせのコメントとシステムコメントを結合し400文字だけ抽出 */
  protected get combinedComment() {
    const comment = this.comment || '';
    const systemComment = Inquiry.pageOptions.systemComment ? `\n${Inquiry.pageOptions.systemComment}` : '';
    return `${comment}${systemComment}`.slice(0, 400);
  }
}

export default Inquiry;
